import { zodResolver } from '@hookform/resolvers/zod';
import { loginSchema } from '@erp/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router';
import type { z } from 'zod';

import { AuthLayout } from '../../components/auth/auth-layout.tsx';
import { PasswordInput } from '../../components/auth/password-input.tsx';
import { Alert } from '../../components/ui/alert.tsx';
import { Button } from '../../components/ui/button.tsx';
import { Field, Input } from '../../components/ui/field.tsx';
import { api } from '../../lib/api.ts';
import { applyApiErrors } from '../../lib/form-errors.ts';
import { safeReturnPath } from '../../lib/navigation.ts';
import { refreshSession } from '../../lib/session.ts';

type Values = z.input<typeof loginSchema>;

export function LoginPage() {
  const [params] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const notice = (location.state as { notice?: string } | null)?.notice;
  const back = safeReturnPath(params.get('volver'));

  const form = useForm<Values, unknown, z.output<typeof loginSchema>>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: params.get('correo') ?? '', password: '' },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      const { mfa } = await api<{ mfa: 'verify' | 'enroll' | 'none' }>('/auth/login', {
        method: 'POST',
        body: values,
      });
      await refreshSession(queryClient);
      const query = back !== '/' ? `?volver=${encodeURIComponent(back)}` : '';
      if (mfa === 'verify') await navigate(`/verificacion${query}`);
      else if (mfa === 'enroll') await navigate(`/activar-2fa${query}`);
      else await navigate(back);
    } catch (err) {
      form.resetField('password');
      setError(applyApiErrors(err, form.setError, ['email', 'password']));
    }
  });

  return (
    <AuthLayout title="Iniciar sesión" subtitle="Entra con el correo de tu cuenta.">
      {notice && <Alert tone="info">{notice}</Alert>}
      {error && <Alert tone="danger">{error}</Alert>}
      <form onSubmit={(event) => void onSubmit(event)} className="space-y-4" noValidate>
        <Field label="Correo electrónico" error={form.formState.errors.email?.message}>
          {(a11y) => (
            <Input
              {...a11y}
              type="email"
              autoComplete="username"
              inputMode="email"
              {...form.register('email')}
            />
          )}
        </Field>
        <Field label="Contraseña" error={form.formState.errors.password?.message}>
          {(a11y) => (
            <PasswordInput
              {...a11y}
              autoComplete="current-password"
              {...form.register('password')}
            />
          )}
        </Field>
        <Button
          variant="brand"
          type="submit"
          className="w-full"
          loading={form.formState.isSubmitting}
        >
          Entrar
        </Button>
      </form>
      <p className="text-center text-sm">
        <Link to="/olvide-contrasena" className="text-primary underline-offset-4 hover:underline">
          ¿Olvidaste tu contraseña?
        </Link>
      </p>
    </AuthLayout>
  );
}
